<template>
  <div class="list-page-container">
    <!-- 顶部操作区：副标题与导出按钮 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-time"></i>
          <span>{{ $t('system.alarmHistoryTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button v-if="hasPermission('sys:alarm:log:export')" type="info" @click="handleExport" icon="el-icon-download" size="small">
            {{ $t('common.export') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 统一控制中心：筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <div class="filter-wrapper">
          <div class="filter-content">
            <el-form :inline="true" :model="logQueryParams" class="filter-form">
              <el-form-item>
                <el-input
                  v-model="logQueryParams.title"
                  :placeholder="$t('system.alarmTitle')"
                  prefix-icon="el-icon-search"
                  clearable
                  class="filter-input"
                  @keyup.enter.native="handleSearchLogs"
                  style="width: 200px"
                />
              </el-form-item>
              <el-form-item>
                <el-select
                  v-model="logQueryParams.category"
                  :placeholder="$t('system.alarmCategory')"
                  clearable
                  style="width: 150px"
                  @change="handleSearchLogs"
                >
                  <el-option :label="$t('system.SYSTEM')" value="SYSTEM" />
                  <el-option :label="$t('system.RESOURCE')" value="RESOURCE" />
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-select
                  v-model="logQueryParams.alertLevel"
                  :placeholder="$t('system.alarmLevel')"
                  clearable
                  style="width: 150px"
                  @change="handleSearchLogs"
                >
                  <el-option :label="$t('system.INFO')" value="info" />
                  <el-option :label="$t('system.WARNING')" value="warning" />
                  <el-option :label="$t('system.ERROR')" value="error" />
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-select
                  v-model="logQueryParams.status"
                  :placeholder="$t('common.status')"
                  clearable
                  style="width: 150px"
                  @change="handleSearchLogs"
                >
                  <el-option :label="$t('system.statusUnresolved')" :value="0" />
                  <el-option :label="$t('system.statusIgnored')" :value="1" />
                  <el-option :label="$t('system.statusResolved')" :value="2" />
                </el-select>
              </el-form-item>
              <el-form-item class="search-btns">
                <el-button type="primary" icon="el-icon-search" @click="handleSearchLogs">{{ $t('common.query') }}</el-button>
                <el-button icon="el-icon-refresh" @click="resetLogQuery">{{ $t('common.reset') }}</el-button>
              </el-form-item>
            </el-form>
          </div>
        </div>

        <div class="action-wrapper" style="margin-top: 15px;">
          <div class="action-buttons">
            <el-dropdown v-if="hasBatchOperationPermission" :disabled="selectedLogIds.length === 0">
              <el-button type="warning" :disabled="selectedLogIds.length === 0" size="small">
                <i class="el-icon-more"></i>
                {{ $t('system.batchOperations') }}
                <i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-if="hasPermission('sys:alarm:log:update')" @click.native="handleBatchResolve">
                  <i class="el-icon-check"></i> {{ $t('system.batchResolveAlarms') }}
                </el-dropdown-item>
                <el-dropdown-item v-if="hasPermission('sys:alarm:log:delete')" @click.native="handleBatchDeleteLogs">
                  <i class="el-icon-delete"></i> {{ $t('system.batchDeleteAlarms') }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </div>
    </div>

    <!-- 告警列表 -->
    <el-card class="list-card" shadow="never">
      <el-table
        :data="historyList"
        v-loading="logsLoading"
        height="100%"
        style="width: 100%"
        class="custom-table"
        @selection-change="handleLogSelectionChange"
        :element-loading-text="$t('common.loading')"
      >
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column prop="title" :label="$t('system.alarmTitle')" min-width="180" show-overflow-tooltip />
        <el-table-column prop="category" :label="$t('system.alarmCategory')" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.category === 'SYSTEM' ? 'warning' : 'info'" size="mini" effect="dark">
              {{ $t('system.' + scope.row.category) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="alertLevel" :label="$t('system.alarmLevel')" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="getLevelType(scope.row.alertLevel)" size="mini" effect="dark">
              {{ $t('system.' + (scope.row.alertLevel ? scope.row.alertLevel.toUpperCase() : 'INFO')) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="alertType" :label="$t('system.alarmType')" width="150" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" effect="plain">
              {{ $t('system.' + scope.row.alertType) || scope.row.alertType }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createDate" :label="$t('system.triggerTime')" width="160" align="center" />
        <el-table-column prop="status" :label="$t('common.status')" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.status)" size="mini" effect="dark">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.operations')" :width="actionColumnWidth" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button v-if="hasPermission('sys:alarm:log:info')" type="primary" plain size="mini" icon="el-icon-view" @click="handleLogDetail(scope.row)">
                {{ $t('common.detail') }}
              </el-button>
              <el-button v-if="scope.row.status === 0 && hasPermission('sys:alarm:log:update')" type="success" plain size="mini" icon="el-icon-check" @click="handleResolve(scope.row)">
                {{ $t('system.resolve') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <div class="pagination-container">
      <el-pagination
        @size-change="handleLogSizeChange"
        @current-change="handleLogCurrentChange"
        :current-page="logQueryParams.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="logQueryParams.limit"
        layout="total, sizes, prev, pager, next, jumper"
        :total="logTotal"
      />
    </div>

    <!-- 告警日志详情对话框 -->
    <el-dialog :title="$t('system.alarmDetail')" :visible.sync="logDetailVisible" width="600px" append-to-body>
      <el-descriptions :column="1" border>
        <el-descriptions-item :label="$t('system.alarmTitle')">{{ detailLogInfo.title }}</el-descriptions-item>
        <el-descriptions-item :label="$t('system.alarmLevel')">
          <el-tag :type="getLevelType(detailLogInfo.alertLevel)" effect="dark">
            {{ detailLogInfo.alertLevel ? $t('system.' + detailLogInfo.alertLevel.toUpperCase()) : '-' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.alarmCategory')">
          <el-tag :type="detailLogInfo.category === 'SYSTEM' ? 'warning' : 'info'" size="mini" effect="dark">
            {{ detailLogInfo.category ? $t('system.' + detailLogInfo.category) : '-' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.alarmType')">
          <el-tag size="mini" effect="plain">
            {{ detailLogInfo.alertType ? ($t('system.' + detailLogInfo.alertType) || detailLogInfo.alertType) : '-' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.alarmContent')">{{ detailLogInfo.content }}</el-descriptions-item>
        <el-descriptions-item :label="$t('system.triggerTime')">{{ detailLogInfo.createDate }}</el-descriptions-item>
        <el-descriptions-item :label="$t('common.status')">{{ getStatusText(detailLogInfo.status) }}</el-descriptions-item>
        <el-descriptions-item v-if="detailLogInfo.status === 2" :label="$t('system.resolveTime')">{{ detailLogInfo.resolveTime }}</el-descriptions-item>
        <el-descriptions-item :label="$t('common.remark')">{{ detailLogInfo.remark || '-' }}</el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button @click="logDetailVisible = false" size="small">{{ $t('common.close') }}</el-button>
        <el-button v-if="detailLogInfo.status === 0 && hasPermission('sys:alarm:log:update')" type="primary" @click="handleResolve(detailLogInfo)" size="small">{{ $t('system.resolve') }}</el-button>
      </div>
    </el-dialog>

    <!-- 告警处理对话框 -->
    <el-dialog :title="$t('system.resolveAlarm')" :visible.sync="resolveVisible" width="400px" append-to-body>
      <el-form :model="resolveForm" label-width="80px" size="small">
        <el-form-item :label="$t('common.status')">
          <el-radio-group v-model="resolveForm.status">
            <el-radio :label="2">{{ $t('system.statusResolved') }}</el-radio>
            <el-radio :label="1">{{ $t('system.statusIgnored') }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="$t('common.remark')">
          <el-input type="textarea" v-model="resolveForm.remark" rows="3" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="resolveVisible = false" size="small">{{ $t('common.cancel') }}</el-button>
        <el-button v-if="hasPermission('sys:alarm:log:update')" type="primary" @click="submitResolve" size="small">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { 
  getAlarmHistory, resolveAlarm, batchDeleteAlarmHistory, exportAlarmHistory 
} from '@/apis/alarm/alarm'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'AlarmManagement',
  computed: {
    hasBatchOperationPermission() {
      return this.hasPermission('sys:alarm:log:update') || this.hasPermission('sys:alarm:log:delete')
    },
    visibleActionCount() {
      let count = 0
      if (this.hasPermission('sys:alarm:log:info')) count += 1
      if (this.hasPermission('sys:alarm:log:update')) count += 1
      return count
    },
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170 }
      return widthMap[count] || 0
    }
  },
  data() {
    return {
      // 告警日志相关
      logsLoading: false,
      historyList: [],
      logTotal: 0,
      logQueryParams: { page: 1, limit: 10, title: '', category: '', alertLevel: '', status: undefined },
      selectedLogIds: [],
      logDetailVisible: false,
      detailLogInfo: {},
      resolveVisible: false,
      resolveForm: { id: '', status: 2, remark: '' }
    }
  },
  created() {
    this.getLogs()
  },
  methods: {
    hasPermission,
    // --- 告警日志逻辑 ---
    async getLogs() {
      this.logsLoading = true
      try {
        const res = await getAlarmHistory(this.logQueryParams)
        this.historyList = res.list || []
        this.logTotal = res.total || 0
      } finally { this.logsLoading = false }
    },
    handleSearchLogs() { this.logQueryParams.page = 1; this.getLogs() },
    resetLogQuery() { this.logQueryParams = { page: 1, limit: 10, title: '', category: '', alertLevel: '', status: undefined }; this.handleSearchLogs() },
    handleLogSelectionChange(selection) { this.selectedLogIds = selection.map(i => i.id) },
    handleLogSizeChange(v) { this.logQueryParams.limit = v; this.getLogs() },
    handleLogCurrentChange(v) { this.logQueryParams.page = v; this.getLogs() },
    getLevelType(level) {
      if (!level) return 'info'
      const l = level.toLowerCase()
      const map = { info: 'info', warning: 'warning', error: 'danger', critical: 'danger' }
      return map[l] || 'info'
    },
    getStatusType(status) { return { 0: 'danger', 1: 'info', 2: 'success' }[status] || 'info' },
    getStatusText(status) { return { 0: this.$t('system.statusUnresolved'), 1: this.$t('system.statusIgnored'), 2: this.$t('system.statusResolved') }[status] || status },
    handleLogDetail(row) { this.detailLogInfo = row; this.logDetailVisible = true },
    handleResolve(row) { this.resolveForm = { id: row.id, status: 2, remark: '' }; this.resolveVisible = true },
    async submitResolve() {
      await resolveAlarm(this.resolveForm.id, this.resolveForm)
      this.$message.success(this.$t('common.success'))
      this.resolveVisible = false
      this.logDetailVisible = false
      this.getLogs()
    },
    handleBatchResolve() {
      if (this.selectedLogIds.length === 0) return
      this.$confirm(this.$t('system.confirmBatchResolve'), this.$t('common.tips'), { type: 'warning' }).then(async () => {
        await Promise.all(this.selectedLogIds.map(id => resolveAlarm(id, { status: 2, remark: this.$t('system.batchResolve') })))
        this.$message.success(this.$t('common.success')); this.getLogs()
      }).catch(() => {})
    },
    handleBatchDeleteLogs() {
      if (this.selectedLogIds.length === 0) return
      this.$confirm(this.$t('system.confirmBatchDeleteAlarms'), this.$t('common.tips'), { type: 'warning' }).then(async () => {
        await batchDeleteAlarmHistory(this.selectedLogIds)
        this.$message.success(this.$t('common.success')); this.getLogs()
      }).catch(() => {})
    },
    async handleExport() {
      const res = await exportAlarmHistory(this.logQueryParams)
      const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `AlarmLog_${new Date().getTime()}.xlsx`
      link.click()
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/list-page.scss";

/* 告警管理页面特有样式 */
.list-page-container {
  .action-buttons {
    .el-button {
      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
    }
  }
}

@media screen and (max-width: 1400px) {
  .list-page-container .action-buttons {
    gap: 4px;

    .el-button {
      min-width: 50px;
      height: 28px;
      font-size: 12px;
      padding: 0 8px;
    }
  }
}

@media screen and (max-width: 768px) {
  .list-page-container {
    padding: 5px;

    .control-container {
      flex-direction: column;
      align-items: stretch;
    }

    .action-wrapper {
      justify-content: flex-start;
      margin-top: 10px;
    }
  }
}
</style>
